import React, { useState, useEffect } from 'react';
import Modal from '../shared/Modal';
import { useInserts } from '../../hooks/useInserts';
import { Insert } from '../../types/mold';

interface SearchInsertModalProps {
  onClose: () => void;
  onAddInsert: (insertId: number, insert: Insert) => void;
  associatedInsertIds: number[];
}

const ITEMS_PER_PAGE = 10;

const SearchInsertModal: React.FC<SearchInsertModalProps> = ({ onClose, onAddInsert, associatedInsertIds }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredInserts, setFilteredInserts] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { inserts, loading, error, fetchInserts } = useInserts();

  useEffect(() => {
    fetchInserts();
  }, [fetchInserts]);

  useEffect(() => {
    const filtered = inserts.filter(
      (insert) =>
        !associatedInsertIds.includes(insert.id) &&
        (insert.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          String(insert.id).includes(searchTerm))
    );
    setFilteredInserts(filtered);
    setCurrentPage(1);
  }, [searchTerm, inserts, associatedInsertIds]);

  const paginatedInserts = filteredInserts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const totalPages = Math.ceil(filteredInserts.length / ITEMS_PER_PAGE);

  return (
    <Modal onClose={onClose} isBackgroundTransparent={true} testId='insert-manager-modal'>
      <div className="bg-white w-full max-w-md px-3">
        <h2 className="text-2xl font-bold mb-2 text-center">ค้นหาอินเสิร์ท</h2>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="พิมพ์ชื่อหรือรหัสอินเสิร์ท"
          data-cy="insert-search-input"
          className="w-full px-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
        />
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="flex justify-between items-center py-2 animate-pulse">
                <div className="flex flex-col space-y-1">
                  <div className="h-4 bg-gray-300 rounded w-24"></div>
                  <div className="h-3 bg-gray-300 rounded w-16"></div>
                </div>
                <div className="h-8 bg-gray-300 rounded-full w-16"></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <p className="text-red-500 text-center">{error}</p>
        ) : (
          <div className="mt-1">
            {paginatedInserts.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {paginatedInserts.map((insert) => (
                  <li
                    key={insert.id}
                    data-cy="insert-option"
                    className="flex justify-between items-center py-2">
                    <div className="flex flex-col">
                      <span className="text-base font-medium">{insert.name}</span>
                      <span className="text-sm text-gray-500">ID: {insert.id}</span>
                    </div>
                    <button
                      onClick={() => onAddInsert(insert.id, insert)}
                      className="px-4 py-2 bg-green-600 text-white rounded-full shadow-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors text-base font-medium"
                    >
                      เพิ่ม
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-center text-gray-500">ไม่พบอินเสิร์ท</p>
            )}
          </div>
        )}
        <div className="mt-4 flex justify-between items-center">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-full shadow-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-sm font-medium disabled:opacity-50"
          >
            ก่อนหน้า
          </button>
          <span className="text-sm text-gray-700">
            หน้าที่ {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-full shadow-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-sm font-medium disabled:opacity-50"
          >
            ถัดไป
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SearchInsertModal;
